/**
 * Public marketing site — exact visual match of Strikingly NAC Easy Solution.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import './LandingPage.css';

export const CONTACT_EMAIL = 'nacsoftwareandtechsolutions26@gmail.com';
export const SITE_LOGO =
  'https://custom-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_limit,fl_lossy,h_300,w_300,f_auto,q_auto/26622375/2026-09-30-1f5f2370-upload-logo.png';

export const IMG = {
  hero: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/f_auto,q_auto,w_2000/unsplashcom/photo-1648824572675-5a55d2c04c33',
  ops: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1649424220505-bb300805f35a',
  gst: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1613433049515-d3a740fb5bb2',
  branches: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1678458536606-f502cab098b1',
  pos: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1648824572675-5a55d2c04c33',
  inventory: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1586812802334-cc9ecc061bd4',
  purchase: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1608976288298-d73b618288ee',
  sales: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1613161609348-c8c67f152b3c',
  quote1: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1649424221028-8e7d31f2e3c9',
  quote2: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1641437741740-900ee30d04e0',
  quote3: 'https://user-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_clip,w_960,h_720,f_auto,q_auto/unsplashcom/photo-1649424219286-56af1d8ee0cb',
};

const SLIDES = [
  {
    title: 'Stop guessing. Know what sold today.',
    text: 'Shop owners use NAC Easy Solution to see sales in Nu., stock on hand, and what to reorder — without spreadsheets or paper books.',
    cta: 'Register your shop',
    href: `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Register my shop — NAC Easy Solution')}`,
    image: IMG.ops,
  },
  {
    title: 'Checkout fast. Stay GST-ready.',
    text: 'Cashiers sell by cash, card, or mobile pay. You get Bhutan GST totals and a tax invoice when your shop has a TPN.',
    cta: 'See solutions',
    href: '/solutions',
    image: IMG.pos,
  },
  {
    title: 'One system for every branch',
    text: 'Open in Thimphu, Paro, or Phuentsholing — keep stock and sales under one login so managers and owners stay in control.',
    cta: 'Talk to us',
    href: `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('I want NAC Easy Solution for my shop')}`,
    image: IMG.branches,
  },
];

const BENEFITS = [
  {
    title: 'Sell at the till',
    text: 'POS built for busy counters — scan, pay in Nu., print the receipt, move to the next customer.',
  },
  {
    title: 'Know your stock',
    text: 'See what is on the shelf, what is low, and what arrived — before you over-order or run out.',
  },
  {
    title: 'Trust your numbers',
    text: 'Sales reports and Bhutan GST in one place so owners decide with facts, not guesswork.',
  },
];

const FEATURES = [
  {
    title: 'Point of Sale (POS)',
    text: 'Your staff check out customers in Nu. — cash, card, mobile pay, split bills, or credit — with receipts they can trust.',
    cta: 'Start with POS',
    to: '/solutions',
    image: IMG.pos,
    wide: true,
  },
  {
    title: 'Inventory & Products',
    text: 'Every item has a name, barcode, Nu. price, and GST type. Stock updates when you sell or receive goods.',
    cta: 'Control inventory',
    to: '/solutions',
    image: IMG.inventory,
    wide: false,
  },
  {
    title: 'Purchase Orders & Goods Receipt',
    text: 'Order from vendors, track what is still due, and only raise stock when goods land — full or partial.',
    cta: 'Buy smarter',
    to: '/solutions',
    image: IMG.purchase,
    wide: false,
  },
  {
    title: 'Sales Reports & Bhutan GST',
    text: 'See today’s sales, top items, and GST — print a tax invoice when you have a TPN, or a normal receipt when you do not.',
    cta: 'See your reports',
    to: '/solutions',
    image: IMG.sales,
    wide: true,
  },
];

const QUOTES = [
  {
    title: 'We finally know stock and sales',
    text: 'Before NAC we used notebooks. Now our Thimphu shop sees sales in Nu. and stock in real time — fewer mistakes at the till.',
    cta: 'Read more',
    to: '/blog',
    image: IMG.quote1,
  },
  {
    title: 'Staff learned it in a day',
    text: 'Cashiers picked up the POS quickly. Customers move faster, and Bhutan GST no longer slows us down.',
    cta: 'Why shops switch',
    to: '/blog',
    image: IMG.quote2,
  },
  {
    title: 'Support that knows Bhutan shops',
    text: 'When we need help, someone answers who understands our business — we stay open and selling.',
    cta: 'Contact us',
    href: `mailto:${CONTACT_EMAIL}`,
    image: IMG.quote3,
  },
];

type ShellProps = {
  children: React.ReactNode;
  title?: string;
  description?: string;
  overlayNav?: boolean;
};

export const MarketingShell: React.FC<ShellProps> = ({
  children,
  title = 'NAC Easy Solution',
  description = 'For Bhutanese shop owners: sell at the till in Nu., know your stock, and stay GST-ready — POS, inventory, and reports in one system.',
  overlayNav = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!overlayNav) {
      setScrolled(false);
      return undefined;
    }
    const root = document.querySelector('.sk-site') as HTMLElement | null;
    if (!root) return undefined;

    const onScroll = () => setScrolled(root.scrollTop > 48);
    root.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener('scroll', onScroll);
  }, [overlayNav]);

  const navSolid = !overlayNav || scrolled;

  return (
    <div className={`sk-site${overlayNav ? ' sk-overlay-nav' : ''}${navSolid ? ' sk-nav-solid' : ' sk-nav-transparent'}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href="https://nacsoftware.online/" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "NAC Easy Solution",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "url": "https://nacsoftware.online/",
          "description": "Bhutan POS and inventory software for shops. Sell in Nu., track stock, and stay ready for Bhutan GST.",
          "provider": {
            "@type": "Organization",
            "name": "NAC Software and Tech Solutions",
            "url": "https://nacsoftware.online/",
            "email": "nacsoftwareandtechsolutions26@gmail.com"
          }
        }`}</script>
      </Helmet>

      <header className="sk-header">
        <Link to="/" className="sk-brand" onClick={() => setMenuOpen(false)}>
          <img src={SITE_LOGO} alt="NAC" className="sk-logo" />
          <span>NAC Easy Solution</span>
        </Link>
        <nav className="sk-nav" aria-label="Main">
          <NavLink to="/" end onClick={() => setMenuOpen(false)}>Home</NavLink>
          <NavLink to="/about" onClick={() => setMenuOpen(false)}>About Us</NavLink>
          <NavLink to="/solutions" onClick={() => setMenuOpen(false)}>Solutions</NavLink>
          <NavLink to="/support" onClick={() => setMenuOpen(false)}>Support</NavLink>
          <NavLink to="/blog" onClick={() => setMenuOpen(false)}>Blog &amp; Resources</NavLink>
        </nav>
        <button type="button" className="sk-burger" aria-label="Menu" onClick={() => setMenuOpen((v) => !v)}>
          <MenuOutlined />
        </button>
      </header>

      {menuOpen && (
        <div className="sk-drawer">
          <NavLink to="/" end onClick={() => setMenuOpen(false)}>Home</NavLink>
          <NavLink to="/about" onClick={() => setMenuOpen(false)}>About Us</NavLink>
          <NavLink to="/solutions" onClick={() => setMenuOpen(false)}>Solutions</NavLink>
          <NavLink to="/support" onClick={() => setMenuOpen(false)}>Support</NavLink>
          <NavLink to="/blog" onClick={() => setMenuOpen(false)}>Blog &amp; Resources</NavLink>
          <button type="button" onClick={() => { setMenuOpen(false); navigate('/user/login'); }}>Staff login</button>
          <button type="button" onClick={() => { setMenuOpen(false); navigate('/admin/login'); }}>Admin login</button>
        </div>
      )}

      <main>{children}</main>

      <footer className="sk-footer">
        <p>©{new Date().getFullYear()} - NAC Software and Tech Solutions. Made in Bhutan.</p>
        <div className="sk-footer-links">
          <Link to="/terms">Terms of Use</Link>
          <Link to="/billing">Billing &amp; Payment</Link>
          <Link to="/user/login">Staff login</Link>
          <Link to="/admin/login">Admin login</Link>
          <a href={`mailto:${CONTACT_EMAIL}`}>Contact Us</a>
        </div>
      </footer>
    </div>
  );
};

const LandingPage: React.FC = () => {
  const [slide, setSlide] = useState(0);
  const current = SLIDES[slide];

  const prev = () => setSlide((s) => (s + SLIDES.length - 1) % SLIDES.length);
  const next = () => setSlide((s) => (s + 1) % SLIDES.length);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSlide((s) => (s + 1) % SLIDES.length);
    }, 14000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <MarketingShell
      title="Bhutan POS and Inventory Software | NAC Easy Solution"
      description="Bhutan POS and inventory software for shops. Sell in Nu., track stock, purchase orders, sales reports, and Bhutan GST with NAC Easy Solution."
      overlayNav
    >
      <section className="sk-hero">
        <video
          className="sk-hero-bg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={`${process.env.PUBLIC_URL || ''}/landing/login_photo_staff.jpg`}
          aria-label="Checkout playing behind the headline"
        >
          <source src={`${process.env.PUBLIC_URL || ''}/landing/pos-checkout.mp4`} type="video/mp4" />
        </video>
        <div className="sk-hero-row">
        <div className="sk-hero-inner">
          <p className="sk-hero-kicker">Bhutan POS and inventory software</p>
          <h1>Sell faster. Know your stock.<br />Grow with confidence.</h1>
          <p className="sk-hero-sub">
            NAC Easy Solution is Bhutan POS and inventory software for shops. Check out customers in Nu., see stock on hand, and stay ready for Bhutan GST.
          </p>
          <div className="sk-hero-actions">
            <a
              className="sk-btn"
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Register my shop — NAC Easy Solution')}`}
            >
              Register your shop
            </a>
            <Link className="sk-btn sk-btn-ghost" to="/solutions">
              See how it works
            </Link>
          </div>
        </div>
        </div>
      </section>

      <section className="sk-benefits">
        <div className="sk-benefits-inner">
          <div className="sk-section-head">
            <h2>Built so business owners can grasp it — and use it daily</h2>
            <p>Clear tools for the till, the storeroom, and the owner’s desk. Start with what you need; grow when you are ready.</p>
          </div>
          <div className="sk-benefits-grid">
            {BENEFITS.map((item) => (
              <article key={item.title} className="sk-benefit">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sk-slideshow">
        <button type="button" className="sk-slide-arrow sk-slide-prev" onClick={prev} aria-label="Previous">‹</button>
        <div className="sk-slide" key={slide}>
          <div className="sk-slide-copy">
            <h2>{current.title}</h2>
            <p>{current.text}</p>
            {current.href.startsWith('mailto:') ? (
              <a className="sk-btn" href={current.href}>{current.cta}</a>
            ) : (
              <Link className="sk-btn" to={current.href}>{current.cta}</Link>
            )}
          </div>
          <div className="sk-slide-media" style={{ backgroundImage: `url(${current.image})` }} />
        </div>
        <button type="button" className="sk-slide-arrow sk-slide-next" onClick={next} aria-label="Next">›</button>
        <div className="sk-slide-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`sk-dot${i === slide ? ' is-active' : ''}`}
              aria-label={`Slide ${i + 1}`}
              onClick={() => setSlide(i)}
            />
          ))}
        </div>
      </section>

      <section className="sk-features">
        <div className="sk-features-inner">
          <div className="sk-section-head">
            <h2>Everything your shop needs to run</h2>
            <p>POS, inventory, purchasing, sales reports, and Bhutan GST — one system shop owners and staff actually use.</p>
          </div>
          <div className="sk-features-grid">
            {FEATURES.map((item) => (
              <article key={item.title} className="sk-feature-card">
                <div
                  className="sk-feature-media"
                  style={{ backgroundImage: `url(${item.image})` }}
                  role="img"
                  aria-label={item.title}
                />
                <div className="sk-feature-body">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <Link to={item.to} className="sk-feature-link">
                    {item.cta}
                    <span aria-hidden>→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sk-section sk-quotes">
        <div className="sk-section-head">
          <h2>Why Bhutanese shop owners switch</h2>
          <p>Real reasons businesses choose NAC Easy Solution for inventory, POS, and daily sales.</p>
        </div>
        <div className="sk-grid-3">
          {QUOTES.map((q) => (
            <article key={q.title} className="sk-card">
              <div className="sk-card-img" style={{ backgroundImage: `url(${q.image})` }} />
              <h3>{q.title}</h3>
              <p>{q.text}</p>
              {'href' in q && q.href ? (
                <a className="sk-btn" href={q.href}>{q.cta}</a>
              ) : (
                <Link className="sk-btn" to={q.to || '/'}>{q.cta}</Link>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="sk-cta-band">
        <div className="sk-cta-band-inner">
          <h2>Ready to run your shop on one system?</h2>
          <p>
            Tell us your shop name, branches, and TPN if you have one. We will help you start with POS, inventory, and the features you need.
          </p>
          <div className="sk-hero-actions">
            <a
              className="sk-btn"
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Register my shop — NAC Easy Solution')}`}
            >
              Register your shop
            </a>
            <Link className="sk-btn sk-btn-ghost sk-btn-ghost-dark" to="/admin/login">
              Admin login
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default LandingPage;
